import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { RUNS_KEY, useForcePlaceOverlaps } from "../api/use-runs";
import { formatEntityLabel } from "../lib/format-entity-label";
import type { ForcePlaceResult, ForcePlaceSkip } from "../types";

/** Extracts the API `errorCode` from a thrown Axios error, if present. */
function apiErrorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { errorCode?: string } } })?.response
    ?.data?.errorCode;
}

interface ForcePlaceModalProps {
  scheduleId: string;
  /** The latest (Succeeded) run to force-place against. */
  runId: string;
  /** Overlap tolerance (minutes) chosen on the validation-issues page. */
  toleranceMinutes: number;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Preview-then-confirm modal for the "accept small overlaps" bulk action. On
 * open it fires a dry-run preview (writes nothing) and renders the plan
 * — "N students → M sections, X skipped". Confirm commits the placements.
 *
 * Built on the native `<dialog>` element for a11y (focus trap, ESC, backdrop),
 * mirroring {@link ReassignModal}.
 */
export function ForcePlaceModal({
  scheduleId,
  runId,
  toleranceMinutes,
  isOpen,
  onClose,
}: ForcePlaceModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const qc = useQueryClient();
  const preview = useForcePlaceOverlaps(scheduleId);
  const apply = useForcePlaceOverlaps(scheduleId);
  // The preview mutation's own `data` is the plan; we read it directly.
  const plan = preview.data ?? null;

  // Sync the native dialog's open/close state with the `isOpen` prop.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      lastFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // (Re)build the dry-run preview whenever the modal is open — on first open and
  // if the run or tolerance changes. `preview` is intentionally excluded from
  // deps (its identity is stable; including it would re-fire every render).
  useEffect(() => {
    if (!isOpen) return;
    preview.reset();
    preview.mutate({ runId, request: { toleranceMinutes, dryRun: true } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, runId, toleranceMinutes]);

  // Restore focus to the trigger after close.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => {
      window.setTimeout(() => {
        lastFocusedRef.current?.focus?.();
        lastFocusedRef.current = null;
      }, 0);
    };
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, []);

  // ESC / cancel → onClose (jsdom doesn't route `cancel` reliably).
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dialog.open) {
        e.preventDefault();
        onClose();
      }
    };
    dialog.addEventListener("cancel", handleCancel);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    if (apply.isPending) return;
    apply.mutate(
      { runId, request: { toleranceMinutes, dryRun: false } },
      {
        onSuccess: (result) => {
          toast.success(placementSummary(result));
          onClose();
        },
        onError: (err) => {
          // A raced re-run/refresh between preview and confirm makes this run
          // stale; the backend rejects it (no partial write). Refresh the runs
          // cache so the page picks up the new latest run, tell the operator,
          // and close so re-opening previews against the current run.
          const code = apiErrorCode(err);
          if (
            code === "override.not_latest_run" ||
            code === "override.run_not_succeeded"
          ) {
            qc.invalidateQueries({ queryKey: RUNS_KEY(scheduleId) });
            toast.error(
              "The schedule changed since this preview. Re-open “Accept small overlaps” to review the current plan.",
            );
            onClose();
            return;
          }
          toast.error(
            err instanceof Error ? err.message : "Failed to place students.",
          );
        },
      },
    );
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) onClose();
  };

  const eligibleCount = plan?.placed.length ?? 0;
  const canConfirm =
    !!plan && eligibleCount > 0 && !apply.isPending && !preview.isPending;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="force-place-modal-title"
      aria-modal="true"
      data-testid="force-place-modal"
      onClick={handleBackdropClick}
      className="m-auto bg-surface border border-border-subtle rounded-lg w-full max-w-[520px] max-h-[86vh] p-0 text-text-soft animate-fade-in [&::backdrop]:bg-black/50"
    >
      <div className="flex flex-col max-h-[86vh]">
        <div className="flex items-start justify-between gap-3 px-5 py-[18px] border-b border-border-subtle">
          <div className="min-w-0">
            <h3
              id="force-place-modal-title"
              data-testid="force-place-modal-title"
              className="m-0 text-[17px] font-semibold text-white"
            >
              Accept overlaps up to {toleranceMinutes}{" "}
              {toleranceMinutes === 1 ? "minute" : "minutes"}
            </h3>
            <p className="mt-1 text-[13px] text-text-muted">
              Places students left unplaced only because a course overlaps
              another within the tolerance.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="force-place-modal-close"
            className="text-text-muted hover:text-white text-[20px] leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {preview.isPending ? (
            <div
              data-testid="force-place-modal-loading"
              className="py-10 flex justify-center"
            >
              <LoadingSpinner size="lg" />
            </div>
          ) : preview.isError ? (
            <div
              data-testid="force-place-modal-error"
              className="bg-page border border-red-500/30 rounded-md p-4 text-[13px] text-red-400 flex items-center justify-between gap-3"
            >
              <span>
                {preview.error instanceof Error
                  ? preview.error.message
                  : "Failed to build the preview."}
              </span>
              <button
                type="button"
                onClick={() =>
                  preview.mutate({
                    runId,
                    request: { toleranceMinutes, dryRun: true },
                  })
                }
                data-testid="force-place-modal-retry"
                className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-3 py-1.5 text-[12px] font-medium"
              >
                Retry
              </button>
            </div>
          ) : plan && eligibleCount === 0 ? (
            <div
              data-testid="force-place-modal-empty"
              className="bg-page border border-border-subtle rounded-md p-4 text-[13px] text-text-muted"
            >
              No students are eligible at a {toleranceMinutes}-minute tolerance.
              Adjust the tolerance and try again.
            </div>
          ) : plan ? (
            <ForcePlaceSummary plan={plan} />
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-border-subtle">
          <span
            data-testid="force-place-modal-footer-note"
            className="text-[12px] text-text-faint"
          >
            No undo — a full re-run of the scheduler clears these.
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="force-place-modal-cancel"
              className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-4 py-2 text-[14px] font-medium cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="force-place-modal-confirm"
              className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md px-4 py-2 text-[14px] font-medium cursor-pointer"
            >
              {apply.isPending && <LoadingSpinner size="sm" />}
              {apply.isPending
                ? "Placing…"
                : `Place ${eligibleCount} ${
                    eligibleCount === 1 ? "student" : "students"
                  }`}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}

function ForcePlaceSummary({ plan }: { plan: ForcePlaceResult }) {
  const sectionCount = new Set(
    plan.placed.map((p) => `${p.normalizedCourseId}|${p.sectionLabel ?? ""}`),
  ).size;

  return (
    <div className="flex flex-col gap-3" data-testid="force-place-modal-summary">
      <p className="text-[14px] text-text-soft">
        <strong className="text-white">{plan.placed.length}</strong>{" "}
        {plan.placed.length === 1 ? "student" : "students"} will be placed into{" "}
        <strong className="text-white">{sectionCount}</strong>{" "}
        {sectionCount === 1 ? "section" : "sections"}.
      </p>

      {plan.skippedForCapacity.length > 0 && (
        <SkipList
          testId="force-place-modal-skipped-capacity"
          title={`${plan.skippedForCapacity.length} skipped — section at capacity`}
          skips={plan.skippedForCapacity}
        />
      )}
      {plan.skippedForLinkedCourse.length > 0 && (
        <SkipList
          testId="force-place-modal-skipped-linked"
          title={`${plan.skippedForLinkedCourse.length} skipped — linked-course requirement`}
          skips={plan.skippedForLinkedCourse}
        />
      )}
    </div>
  );
}

function SkipList({
  testId,
  title,
  skips,
}: {
  testId: string;
  title: string;
  skips: ForcePlaceSkip[];
}) {
  return (
    <div
      data-testid={testId}
      className="bg-page border border-amber-500/30 rounded-md p-3"
    >
      <p className="text-[12px] font-semibold text-amber-400 mb-1.5">{title}</p>
      <ul className="flex flex-col gap-1 text-[12px] text-text-muted">
        {skips.map((s) => (
          <li key={`${s.studentIdentifier}::${s.courseId}::${s.sectionLabel ?? ""}`}>
            {formatEntityLabel(s.studentName, s.studentIdentifier)} —{" "}
            {formatEntityLabel(null, s.courseId)}
            {s.sectionLabel ? ` · Sec ${s.sectionLabel}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

function placementSummary(result: ForcePlaceResult): string {
  const skipped =
    result.skippedForCapacity.length + result.skippedForLinkedCourse.length;
  const base = `Placed ${result.placed.length} ${
    result.placed.length === 1 ? "student" : "students"
  }.`;
  return skipped > 0 ? `${base} ${skipped} skipped.` : base;
}
